import React, { useState, useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import { useCmdrsForm } from '../useCmdrsForm';
import Step1, { Step1CmdrsData } from './steps/step1';
import Step2, { Step2CmdrsData } from './steps/step2';
import Step3, { Step3CmdrsData } from './steps/step3';
import Step4, { Step4CmdrsData } from './steps/step4';
import Step5, { Step5CmdrsData } from './steps/step5';
import ContainerPainel from 'components/container_painel';
import { FormCmdrsHorizontalStepper } from 'components/forms/form_cmdrs_horizontal_stepper';
import { useNavigate, useParams } from 'react-router-dom';
import { updateFormularioCmdrsByUUID, getFormularioCmdrsByUUID } from 'api/forms/pacote_formularios.api';
import { mapToCreateCmdrsDto } from './utils/mapToCreateCmdrsDto';

const FormularioCMDRSEditar: React.FC = () => {
  const { activeStep, handleNext, handleBack } = useCmdrsForm();
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();

  const [Step1CmdrsData, setStep1CmdrsData] = useState<Step1CmdrsData>({
    hasCmdrs: null,
    instrument: '',
    instrumentNumber: '',
    publicationDate: '',
    document_uuid: '',
    status: '',
    structure: '',
    presidingInstitution: '',
    mandateEnd: '',
    qtdPoderPublico: 0,
    qtdSociedadeCivil: 0,
  });

  const [Step2CmdrsData, setStep2CmdrsData] = useState<Step2CmdrsData>({
    members: []
  });

  const [Step3CmdrsData, setStep3CmdrsData] = useState<Step3CmdrsData>({
    existingCouncils: [],
    otherExistingCouncil: '',
    seatedCouncils: [],
    otherSeatedCouncil: '',
    isConsortiumMember: null,
    participatesActivities: null,
    activitiesDescription: '',
    hasSusaf: null,
  });

  const [Step4CmdrsData, setStep4CmdrsData] = useState<Step4CmdrsData>({
    selectedChallenges: [],
    challengesOrder: [],
    otherChallenge: '',
  });

  const [Step5CmdrsData, setStep5CmdrsData] = useState<Step5CmdrsData>({
    demands: [],
  });


  useEffect(() => {
    if (uuid) {
      fetchCmdrsFormData(uuid).then((formData) => {
        setStep1CmdrsData(formData.step1);
        setStep2CmdrsData(formData.step2);
        setStep3CmdrsData(formData.step3);
        setStep4CmdrsData(formData.step4);
        setStep5CmdrsData(formData.step5);
      });
    }
  }, [uuid]);

  const hasCmdrs = Step1CmdrsData.hasCmdrs ?? null;

  const handleSubmit = () => {
    const isStep1Filled = !!Step1CmdrsData.hasCmdrs;
    const isStep2Filled = Step2CmdrsData.members && Step2CmdrsData.members.length > 0;
    const isStep3Filled = Step3CmdrsData.existingCouncils.length > 0 || Step3CmdrsData.seatedCouncils.length > 0;
    const isStep4Filled = Step4CmdrsData.selectedChallenges.length > 0;
    const isStep5Filled = Step5CmdrsData.demands && Step5CmdrsData.demands.length > 0;

    let package_status: number | undefined = undefined;
    if (isStep1Filled && isStep2Filled && isStep3Filled && isStep4Filled && isStep5Filled) {
      package_status = 3;
    } else if (isStep1Filled && isStep2Filled && isStep3Filled && isStep4Filled) {
      package_status = 2;
    }

    if (!hasCmdrs) {
      package_status = 3;
    }

    const payload = mapToCreateCmdrsDto({
      step1: Step1CmdrsData,
      step2: Step2CmdrsData,
      step3: Step3CmdrsData,
      step4: Step4CmdrsData,
      step5: Step5CmdrsData,
      package_status,
    });


    if (uuid) {
      updateFormularioCmdrsByUUID(uuid, payload);
      if (activeStep === 4) {
        navigate('/pacote-de-formularios');
      }
    }

    if (hasCmdrs) {
      handleNext();
    }

    if (!hasCmdrs) {
      navigate('/pacote-de-formularios');
    }
  };


  return (
    <ContainerPainel pagina="Informações sobre o CMDRS" showBackButton={true}>
      <Stack marginX={'4rem'} paddingTop={'2rem'}>
        <FormCmdrsHorizontalStepper
          activeStep={activeStep + 1}
          edit={true}
          hasCmdrs={hasCmdrs}
        />

        <Box mt={2}>
          {activeStep === 0 && (
            <Step1
              onNext={handleSubmit}
              onChange={setStep1CmdrsData}
              data={Step1CmdrsData}
            />
          )}

          {hasCmdrs !== false && activeStep === 1 && (
            <Step2
              onNext={handleSubmit}
              onBack={handleBack}
              hasCmdrs={hasCmdrs}
              onChange={setStep2CmdrsData}
              data={Step2CmdrsData}
            />
          )}
          {hasCmdrs !== false && activeStep === 2 && (
            <Step3
              onNext={handleSubmit}
              onBack={handleBack}
              hasCmdrs={hasCmdrs}
              onChange={setStep3CmdrsData}
              data={Step3CmdrsData}
            />
          )}
          {hasCmdrs !== false && activeStep === 3 && (
            <Step4
              onNext={handleSubmit}
              onBack={handleBack}
              hasCmdrs={hasCmdrs}
              onChange={setStep4CmdrsData}
              data={Step4CmdrsData}
            />
          )}
          {hasCmdrs !== false && activeStep === 4 && (
            <Step5
              onNext={handleSubmit}
              onBack={handleBack}
              hasCmdrs={hasCmdrs}
              onChange={setStep5CmdrsData}
              data={Step5CmdrsData}
            />
          )}
        </Box>
      </Stack>
    </ContainerPainel>
  );
};

function formatDate(dateValue: string | Date | null | undefined): string {
  if (!dateValue) return '';
  const d = new Date(dateValue);
  return d.toISOString().slice(0, 10);
}

async function fetchCmdrsFormData(uuid: string) {
  const data = await getFormularioCmdrsByUUID(uuid);

  return {
    step1: {
      hasCmdrs: data.has_cmdrs ?? null,
      instrument: data.instrument ?? '',
      instrumentNumber: data.instrument_number ?? '',
      publicationDate: formatDate(data.publication_date),
      document_uuid: data.document_uuid ?? '',
      status: data.status ?? '',
      structure: data.structure ?? '',
      presidingInstitution: data.presiding_institution ?? '',
      mandateEnd: formatDate(data.mandate_end),
      qtdPoderPublico: data.qtd_poder_publico ?? 0,
      qtdSociedadeCivil: data.qtd_sociedade_civil ?? 0,
    },
    step2: {
      members: data.members ?? [],
    },
    step3: {
      existingCouncils: data.existing_councils ?? [],
      otherExistingCouncil: data.other_existing_council ?? '',
      seatedCouncils: data.seated_councils ?? [],
      otherSeatedCouncil: data.other_seated_council ?? '',
      isConsortiumMember: typeof data.is_consortium_member === 'boolean' ? data.is_consortium_member : null,
      participatesActivities: typeof data.participates_activities === 'boolean' ? data.participates_activities : null,
      activitiesDescription: data.activities_description ?? '',
      hasSusaf: typeof data.has_susaf === 'boolean' ? data.has_susaf : null,
    },
    step4: {
      selectedChallenges: data.selected_challenges ?? [],
      challengesOrder: data.challenges_order ?? [],
      otherChallenge: data.other_challenge ?? '',
    },
    step5: {
      demands: data.demands ?? [],
    },
  };
}

export default FormularioCMDRSEditar;